import React, {useEffect, useState, useRef, KeyboardEvent} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {
    getRawDate,
    isValid,
    getCurrentAmPm,
    getRawTime,
    isValidTime
} from '@priority-software/priority-utils/lib/date';
import {
    autocompleteFromCurrentDate,
    getStringFormatByColumnType,
    isAutoCompleteApplicable,
    isMaxLengthReached,
    formatDate,
    formatStringToDate,
    isDate,
    isDateTime
} from '../../../utils/date';
import {isTime, isHour12Format, formatStringToTimeObj} from '../../../utils/time';
import {keys} from '../../../../../constants/KeyCodes';
import direction from '../../../styles/direction.module.scss';
import {getLang} from '../../../../../Localization';
import {StringInputProps} from '../StringInput';
import {FormDatePicker} from './DatePicker';

export const DateInput = ({
    column,
    value,
    handleMouseDown,
    handleChange,
    handleKeyDown,
    inputRef,
    searchMode,
    isReadonly,
    onFocus,
    onBlur
}: StringInputProps) => {
    const periodAutoUpdated = useRef(false);
    const isRemoving = useRef(false);
    const lang = getLang();

    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

    useEffect(() => {
        if (periodAutoUpdated.current) {
            inputRef.current.setSelectionRange(value?.length - 2, value?.length);
            periodAutoUpdated.current = false;
        }
    }, [value, inputRef]);

    const handleDatePickerState = (isOpen: boolean) => {
        if (isDatepickerOpen !== isOpen) {
            setIsDatepickerOpen(isOpen);
            if (!isOpen) {
                column.setEditMode(false);
            }
        }
    };

    const handleClick = () => {
        setIsDatepickerOpen(false);
    };

    const isValidDateInput = (value: string, format: string): boolean => {
        if (column.editMode || searchMode) {
            return true;
        } else {
            return isValid(value, format) && !value.includes('?') && !value.includes('*');
        }
    };

    const getRawDateTime = (newValue: string, format: string) => {
        let rawDateTime = newValue;
        const oldValue = !isRemoving.current && value?.length > newValue.length ? '' : value;
        if (!column.editMode) {
            if (isDate(column.Type)) {
                rawDateTime = getRawDate(
                    oldValue,
                    newValue,
                    format,
                    column.Type === priority.netitems.form.ColumnType.Date14
                );
            } else {
                rawDateTime = getRawTime(newValue, oldValue, format);
            }
        }
        return rawDateTime;
    };

    const isValidDateTime = (value: string, format: string) => {
        if (column.editMode || searchMode) {
            return true;
        } else if (!value.includes('?') && !value.includes('*')) {
            if (isDate(column.Type)) {
                return isValidDateInput(value, format);
            } else {
                return isValidTime(value, format);
            }
        }
        return false;
    };

    const validateAndChange = event => {
        const newValue: string = event.target.value;
        const format = getStringFormatByColumnType(column.Type, lang);
        let rawDate = getRawDateTime(newValue, format);
        if (isValidDateTime(rawDate, format)) {
            if (
                !isRemoving.current &&
                !column.editMode &&
                ((column.Type === priority.netitems.form.ColumnType.Date14 &&
                    rawDate?.length === 15) ||
                    (column.Type === priority.netitems.form.ColumnType.Time5 &&
                        rawDate?.length === 6))
            ) {
                rawDate = `${rawDate}${getCurrentAmPm()}`;
                periodAutoUpdated.current = true;
            }
            handleChange(rawDate);
        }
    };

    const processDatePickerTriggers = event => {
        if (isDate(column.Type)) {
            if (event.key === keys.F6) {
                setIsDatepickerOpen(true);
            } else if (event.key === keys.SPACE && (!value || value === '')) {
                setIsDatepickerOpen(true);
            }
        }
    };

    const processKeyDown = (event: KeyboardEvent) => {
        if (!isReadonly) {
            isRemoving.current = event.key === keys.BACKSPACE;

            if (event.key === keys.MINUS || event.key === keys.PLUS) {
                event.preventDefault();
                if (isMaxLengthReached(value, column.Type, lang)) {
                    let newDate;
                    const dateIncrement = event.key === keys.MINUS ? -1 : 1;
                    if (isDate(column.Type)) {
                        newDate = formatStringToDate(value, isDateTime(column.Type), lang);
                        newDate.setDate(newDate.getDate() + dateIncrement);
                    } else {
                        newDate = formatStringToTimeObj(value);
                        newDate.setMinutes(newDate.getMinutes() + dateIncrement);
                    }
                    handleChange(formatDate(newDate, column.Type));
                } else if (isAutoCompleteApplicable(value, column.Type)) {
                    handleChange(autocompleteFromCurrentDate(value, column.Type));
                }
            }

            processDatePickerTriggers(event);
        }
        handleKeyDown(event);
    };

    return (
        <>
            <input
                type={'text'}
                maxLength={
                    isTime(column.Type) && isHour12Format(lang)
                        ? column.maxLength + 3
                        : column.maxLength
                }
                ref={inputRef}
                value={value}
                onKeyDown={processKeyDown}
                onChange={validateAndChange}
                readOnly={isReadonly}
                onMouseDown={handleMouseDown}
                onFocus={onFocus}
                onClick={handleClick}
                className={direction.ltrDirection}
                onBlur={onBlur}
            />
            {isDate(column.Type) && !isReadonly && (
                <FormDatePicker
                    isOpen={isDatepickerOpen}
                    setIsOpen={handleDatePickerState}
                    key={value.toString()}
                    onChange={handleChange}
                    value={value}
                    columnType={column.Type}
                    inputRef={inputRef}
                />
            )}
        </>
    );
};
